import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSiteConfig } from '../components/SiteBanner';
import { applyCachedTheme, syncThemeFromConfig } from '../lib/themeRuntime';

const ThemeContext = createContext(null);
const KEY = 'icd-theme';

function initialTheme() {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {
    // ignore
  }
  // Light mode is the default; dark mode only when the user has chosen it.
  return 'light';
}

// Colour tokens from the admin theme: the cached copy is applied by public/index.html before paint;
// once /api/site-config loads (shared module cache with the banner), apply and re-cache the live one.
function useAdminColourTheme() {
  const config = useSiteConfig();
  useEffect(() => {
    applyCachedTheme();
  }, []);
  useEffect(() => {
    if (config) syncThemeFromConfig(config);
  }, [config]);
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(initialTheme);
  useAdminColourTheme();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    // Keep the browser chrome colour in step with the mode (navy-deep of the active palette).
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--c-navy-deep').trim().split(/\s+/).map(Number);
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta && v.length === 3 && v.every((n) => Number.isFinite(n))) meta.setAttribute('content', `rgb(${v.join(',')})`);
    } catch {
      // cosmetic only
    }
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const toggleTheme = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
